import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ActionsDropdown from "../../components/Common/ActionsDropdown";
import { useConfirm } from "../../contexts/ConfirmContext";
import { baseUrl } from "../../Network";
import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";

function slotList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            return slotList(JSON.parse(value));
        } catch (error) {
            return [];
        }
    }
    if (value && typeof value === "object") return Object.values(value);
    return [];
}

function formatWhen(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function teacherName(row) {
    const name = [row?.teacher?.firstName, row?.teacher?.lastName].filter(Boolean).join(" ");
    return name || "Unassigned";
}

const ManagePersonalClass = () => {
    document.title = "Private lessons | RYD Admin";
    const { showConfirm } = useConfirm();
    const [rows, setRows] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [payingId, setPayingId] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [partnerFilter, setPartnerFilter] = useState("all");
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkTeacherId, setBulkTeacherId] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);
    const [assignRow, setAssignRow] = useState(null);
    const [assignTeacherId, setAssignTeacherId] = useState("");
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [partnerRow, setPartnerRow] = useState(null);
    const [partnerId, setPartnerId] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            const [classRes, teacherRes, partnerRes] = await Promise.all([
                axios.get(`${baseUrl}/admin/personal-class/all`),
                axios.get(`${baseUrl}/admin/personal-class/teachers`),
                axios.get(`${baseUrl}/admin/partner/all`).catch(() => ({ data: { data: [] } })),
            ]);
            const classData = classRes.data?.data;
            const teacherData = teacherRes.data?.data;
            const partnerData = partnerRes.data?.data;
            const partnerList = Array.isArray(partnerData) ? partnerData : (partnerData?.data || []);
            setRows(Array.isArray(classData) ? classData : []);
            setTeachers(Array.isArray(teacherData) ? teacherData : []);
            setPartnerOptions(Array.isArray(partnerList) ? partnerList : []);
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to load private lessons");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const assignTeacher = async (id, teacherId) => {
        try {
            setSavingId(id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/teacher/${id}`, {
                teacherId: teacherId ? Number(teacherId) : null,
            });
            const updated = response.data?.data;
            setRows((current) => current.map((row) => (row.id === id ? updated : row)));
            toast.success("Teacher updated");
            setAssignRow(null);
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to assign teacher");
        } finally {
            setSavingId(null);
        }
    };

    const togglePaid = async (row) => {
        const nextPaid = row.paymentStatus !== "active";
        const confirmed = await showConfirm({
            title: nextPaid ? "Mark paid" : "Mark unpaid",
            message: nextPaid
                ? "Mark this private lesson as paid for the current period?"
                : "Mark this private lesson as unpaid?",
            variant: nextPaid ? "primary" : "danger",
        });
        if (!confirmed) return;
        try {
            setPayingId(row.id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/paid/${row.id}`, {
                isPaid: nextPaid,
            });
            const updated = response.data?.data;
            setRows((current) => current.map((item) => (item.id === row.id ? updated : item)));
            toast.success(nextPaid ? "Marked as paid" : "Marked as unpaid");
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to update payment");
        } finally {
            setPayingId(null);
        }
    };

    const partners = Array.from(new Set(rows.map((row) => row.partner?.organizationName).filter(Boolean)));
    const query = search.trim().toLowerCase();
    const visibleRows = rows.filter((row) => {
        if (statusFilter !== "all" && row.paymentStatus !== statusFilter) return false;
        if (partnerFilter !== "all" && row.partner?.organizationName !== partnerFilter) return false;
        if (!query) return true;
        const slots = slotList(row.slots);
        const haystack = [
            row.child?.firstName,
            row.child?.lastName,
            row.parent?.email,
            row.parentLocation || row.parent?.country,
            row.parentCity || row.parent?.state,
            row.grade,
            row.package?.title,
            row.partner?.organizationName,
            row.teacher?.firstName,
            row.teacher?.lastName,
            ...slots.map((slot) => `${slot.dayText || ""} ${slot.timeText || ""} ${slot.watTimeText || ""}`),
        ].join(" ").toLowerCase();
        return haystack.includes(query);
    });
    const visibleIds = visibleRows.map((row) => row.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    const counts = useMemo(() => ({
        active: rows.filter((row) => row.paymentStatus === "active").length,
        unpaid: rows.filter((row) => row.paymentStatus === "unpaid").length,
        expired: rows.filter((row) => row.paymentStatus === "expired").length,
    }), [rows]);

    const toggleAllVisible = () => {
        setSelectedIds((current) => (
            allVisibleSelected
                ? current.filter((id) => !visibleIds.includes(id))
                : Array.from(new Set([...current, ...visibleIds]))
        ));
    };

    const toggleRow = (id) => {
        setSelectedIds((current) => (
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        ));
    };

    const assignMany = async () => {
        if (selectedIds.length === 0) {
            toast.error("Select at least one lesson");
            return;
        }
        try {
            setBulkSaving(true);
            const response = await axios.put(`${baseUrl}/admin/personal-class/teachers`, {
                ids: selectedIds,
                teacherId: bulkTeacherId ? Number(bulkTeacherId) : null,
            });
            const updated = Array.isArray(response.data?.data) ? response.data.data : [];
            const byId = new Map(updated.map((row) => [row.id, row]));
            setRows((current) => current.map((row) => byId.get(row.id) || row));
            setSelectedIds([]);
            toast.success(`Teacher updated for ${updated.length} lesson${updated.length === 1 ? "" : "s"}`);
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to assign teacher");
        } finally {
            setBulkSaving(false);
        }
    };

    const registrationIsCancelled = (row) => Boolean(row?.cancelledAt || row?.parentCancelled || row?.paymentStatus === "cancelled");

    const cancelRegistration = async (row) => {
        const stillPaid = row.paymentStatus === "active" || (row.isPaid && row.paidUntil && new Date(row.paidUntil) > new Date());
        const confirmed = await showConfirm({
            title: "Cancel registration",
            message: stillPaid
                ? "Cancel this registration? The child can still join until the paid cycle ends."
                : "Cancel this unpaid registration? It will leave the parent's account.",
            variant: "danger",
        });
        if (!confirmed) return;
        try {
            setPayingId(row.id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/cancel/${row.id}`);
            const updated = response.data?.data;
            if (updated) setRows((current) => current.map((item) => (item.id === row.id ? updated : item)));
            toast.success(response.data?.message || "Registration cancelled");
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to cancel registration");
        } finally {
            setPayingId(null);
        }
    };

    const activateRegistration = async (row) => {
        const confirmed = await showConfirm({
            title: "Activate registration",
            message: "Activate this private lesson again?",
            variant: "primary",
        });
        if (!confirmed) return;
        try {
            setPayingId(row.id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/activate/${row.id}`);
            const updated = response.data?.data;
            if (updated) setRows((current) => current.map((item) => (item.id === row.id ? updated : item)));
            toast.success(response.data?.message || "Registration activated");
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to activate registration");
        } finally {
            setPayingId(null);
        }
    };

    const pauseBilling = async (row) => {
        const confirmed = await showConfirm({
            title: "Pause billing",
            message: "Pause this private lesson? Remaining paid time will be saved until you resume.",
            variant: "primary",
        });
        if (!confirmed) return;
        try {
            setPayingId(row.id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/pause/${row.id}`);
            const updated = response.data?.data;
            if (updated) setRows((current) => current.map((item) => (item.id === row.id ? updated : item)));
            toast.success(response.data?.message || "Billing paused");
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to pause billing");
        } finally {
            setPayingId(null);
        }
    };

    const resumeBilling = async (row) => {
        const confirmed = await showConfirm({
            title: "Resume billing",
            message: row.pausedRemainingLabel
                ? `Resume this private lesson? ${row.pausedRemainingLabel} will start counting again.`
                : "Resume this private lesson? Remaining paid time will start counting again.",
            variant: "primary",
        });
        if (!confirmed) return;
        try {
            setPayingId(row.id);
            const response = await axios.put(`${baseUrl}/admin/personal-class/resume/${row.id}`);
            const updated = response.data?.data;
            if (updated) setRows((current) => current.map((item) => (item.id === row.id ? updated : item)));
            toast.success(response.data?.message || "Billing resumed");
        } catch (error) {
            handleAuthError(error);
            toast.error(error?.response?.data?.message || "Unable to resume billing");
        } finally {
            setPayingId(null);
        }
    };

    const statusBadge = (status) => {
        if (status === "paused") return <span className="pl-pill pl-pill-expired">Paused</span>;
        if (status === "cancelled") return <span className="pl-pill pl-pill-unpaid">Cancelled</span>;
        if (status === "active") return <span className="pl-pill pl-pill-active">Active</span>;
        if (status === "renewal_due" || status === "payment_due") return <span className="pl-pill pl-pill-expired">Renewal due</span>;
        if (status === "expired") return <span className="pl-pill pl-pill-expired">Expired</span>;
        return <span className="pl-pill pl-pill-unpaid">Unpaid</span>;
    };

    const openAssign = (row) => {
        setAssignRow(row);
        setAssignTeacherId(String(row.teacherId || row.teacher?.id || ""));
    };

    return (
        <React.Fragment>
            <style>{`
                .pl-shell { border: 0; border-radius: 16px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06); }
                .pl-stat { border: 1px solid #e8eef5; border-radius: 12px; padding: 12px 14px; background: #fff; min-width: 120px; }
                .pl-stat b { display: block; font-size: 20px; line-height: 1.1; color: #0f172a; }
                .pl-stat span { font-size: 12px; color: #64748b; }
                .pl-table { margin-bottom: 0; }
                .pl-table thead th {
                    background: #f8fafc;
                    color: #64748b;
                    font-size: 11px;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    font-weight: 600;
                    border-bottom: 1px solid #e8eef5;
                    white-space: nowrap;
                    padding: 12px 14px;
                }
                .pl-table tbody td { border-color: #f1f5f9; padding: 14px; vertical-align: middle; color: #1e293b; }
                .pl-table tbody tr:hover { background: #f8fbff; }
                .pl-name { font-weight: 600; color: #0f172a; }
                .pl-sub { display: block; margin-top: 2px; color: #64748b; font-size: 12px; }
                .pl-slot { font-size: 13px; line-height: 1.45; }
                .pl-pill { display: inline-flex; align-items: center; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 600; }
                .pl-pill-active { background: #dcfce7; color: #166534; }
                .pl-pill-expired { background: #ffedd5; color: #9a3412; }
                .pl-pill-unpaid { background: #fee2e2; color: #991b1b; }
                .pl-teacher { font-weight: 600; }
                .pl-teacher.is-empty { color: #94a3b8; font-weight: 500; }
            `}</style>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Private lessons" />
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <div className="pl-stat"><b>{rows.length}</b><span>All lessons</span></div>
                        <div className="pl-stat"><b>{counts.active}</b><span>Active</span></div>
                        <div className="pl-stat"><b>{counts.unpaid}</b><span>Unpaid</span></div>
                        <div className="pl-stat"><b>{counts.expired}</b><span>Expired</span></div>
                    </div>
                    <div className="card pl-shell">
                        <div className="card-body">
                            <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                                <div>
                                    <h4 className="mb-1">Private lessons</h4>
                                    <p className="text-muted mb-0">Student, schedule, payment, and teacher. Assign a teacher or mark payment from Actions.</p>
                                </div>
                            </div>
                            <div className="row g-2 mb-3">
                                <div className="col-lg-4">
                                    <input
                                        className="form-control"
                                        placeholder="Search child, email, grade, partner, time"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3 col-lg-2">
                                    <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="all">All statuses</option>
                                        <option value="active">Active</option>
                                        <option value="expired">Expired</option>
                                        <option value="renewal_due">Renewal due</option>
                                        <option value="unpaid">Unpaid</option>
                                        <option value="paused">Paused</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="col-md-4 col-lg-3">
                                    <select className="form-select" value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}>
                                        <option value="all">All partners</option>
                                        {partners.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {selectedIds.length > 0 ? (
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-3 rounded" style={{ background: "#f8fafc" }}>
                                    <span className="fw-semibold">{selectedIds.length} selected</span>
                                    <select
                                        className="form-select"
                                        style={{ maxWidth: 260 }}
                                        value={bulkTeacherId}
                                        onChange={(e) => setBulkTeacherId(e.target.value)}
                                    >
                                        <option value="">Unassigned</option>
                                        {teachers.map((teacher) => (
                                            <option key={teacher.id} value={teacher.id}>
                                                {teacher.firstName} {teacher.lastName}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={bulkSaving}
                                        onClick={assignMany}
                                    >
                                        {bulkSaving ? "Saving…" : "Assign teacher"}
                                    </button>
                                    <button type="button" className="btn btn-light" onClick={() => setSelectedIds([])}>
                                        Clear
                                    </button>
                                </div>
                            ) : null}
                            {loading ? (
                                <p className="text-muted mb-0">Loading…</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table pl-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={allVisibleSelected}
                                                        onChange={toggleAllVisible}
                                                        aria-label="Select all visible lessons"
                                                    />
                                                </th>
                                                <th>Student</th>
                                                <th>Location</th>
                                                <th>City</th>
                                                <th>Grade</th>
                                                <th>Age</th>
                                                <th>Package</th>
                                                <th>Partner</th>
                                                <th>Day</th>
                                                <th>Parent time</th>
                                                <th>WAT</th>
                                                <th>Week</th>
                                                <th>Amount</th>
                                                <th>Paid through</th>
                                                <th>Status</th>
                                                <th>Canceled</th>
                                                <th>Teacher</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={18} className="text-center text-muted py-5">
                                                        No private lessons match this search.
                                                    </td>
                                                </tr>
                                            ) : visibleRows.map((row) => {
                                                const slots = slotList(row.slots);
                                                const paid = row.paymentStatus === "active";
                                                return (
                                                    <tr key={row.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(row.id)}
                                                                onChange={() => toggleRow(row.id)}
                                                                aria-label={`Select ${row.child?.firstName || "lesson"}`}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className="pl-name">{row.child?.firstName} {row.child?.lastName}</span>
                                                            <span className="pl-sub">{row.parent?.email || "—"}</span>
                                                        </td>
                                                        <td>{row.parentLocation || row.parent?.country || "—"}</td>
                                                        <td>{row.parentCity || row.parent?.state || "—"}</td>
                                                        <td>{row.grade || "—"}</td>
                                                        <td>{row.child?.age ?? "—"}</td>
                                                        <td>{row.package?.title || "—"}</td>
                                                        <td>{row.partner?.organizationName || "—"}</td>
                                                        <td>
                                                            {slots.map((slot, index) => (
                                                                <div className="pl-slot" key={index}>{slot.dayText || "—"}</div>
                                                            ))}
                                                        </td>
                                                        <td>
                                                            {slots.map((slot, index) => (
                                                                <div className="pl-slot" key={index}>{slot.timeText || "—"}</div>
                                                            ))}
                                                        </td>
                                                        <td>
                                                            {slots.map((slot, index) => (
                                                                <div className="pl-slot" key={index}>{slot.watTimeText || "—"}</div>
                                                            ))}
                                                        </td>
                                                        <td>{row.classesPerWeek}/wk</td>
                                                        <td>{row.currency} {row.amount}</td>
                                                        <td>
                                                            {row.paymentStatus === "paused"
                                                                ? (row.pausedRemainingLabel || "Paused")
                                                                : (row.cycleEndsLabel || formatWhen(row.paidUntil))}
                                                        </td>
                                                        <td>{statusBadge(row.paymentStatus)}</td>
                                                        <td>{row.parentCancelled ? "Parent canceled" : "—"}</td>
                                                        <td>
                                                            <span className={`pl-teacher ${row.teacher ? "" : "is-empty"}`}>{teacherName(row)}</span>
                                                        </td>
                                                        <td>
                                                            <ActionsDropdown
                                                                loading={savingId === row.id || payingId === row.id}
                                                                toggleTitle="Lesson actions"
                                                                items={[
                                                                    {
                                                                        label: "Assign teacher",
                                                                        icon: "mdi-account-tie",
                                                                        onClick: () => openAssign(row),
                                                                    },
                                                                    {
                                                                        label: "Change partner",
                                                                        icon: "mdi-swap-horizontal",
                                                                        onClick: () => {
                                                                            setPartnerRow(row);
                                                                            setPartnerId(row.partner?.id ? String(row.partner.id) : "");
                                                                        },
                                                                    },
                                                                    row.paymentStatus === "paused"
                                                                        ? null
                                                                        : {
                                                                            label: paid ? "Mark unpaid" : "Mark paid",
                                                                            icon: paid ? "mdi-cash-remove" : "mdi-cash-check",
                                                                            variant: paid ? "danger" : "success",
                                                                            onClick: () => togglePaid(row),
                                                                        },
                                                                    !registrationIsCancelled(row) && row.paymentStatus === "paused"
                                                                        ? {
                                                                            label: "Resume billing",
                                                                            icon: "mdi-play-circle",
                                                                            variant: "success",
                                                                            onClick: () => resumeBilling(row),
                                                                        }
                                                                        : null,
                                                                    !registrationIsCancelled(row) && row.paymentStatus === "active"
                                                                        ? {
                                                                            label: "Pause billing",
                                                                            icon: "mdi-pause-circle",
                                                                            onClick: () => pauseBilling(row),
                                                                        }
                                                                        : null,
                                                                    registrationIsCancelled(row)
                                                                        ? {
                                                                            label: "Activate",
                                                                            icon: "mdi-refresh",
                                                                            variant: "success",
                                                                            onClick: () => activateRegistration(row),
                                                                        }
                                                                        : {
                                                                            label: "Cancel registration",
                                                                            icon: "mdi-close-circle",
                                                                            variant: "danger",
                                                                            onClick: () => cancelRegistration(row),
                                                                        },
                                                                ].filter(Boolean)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </Container>
            </div>
            <Modal isOpen={Boolean(assignRow)} toggle={() => setAssignRow(null)} centered>
                <ModalHeader toggle={() => setAssignRow(null)}>Assign teacher</ModalHeader>
                <ModalBody>
                    <p className="text-muted">
                        {assignRow?.child?.firstName} {assignRow?.child?.lastName}
                    </p>
                    <select
                        className="form-select"
                        value={assignTeacherId}
                        onChange={(e) => setAssignTeacherId(e.target.value)}
                    >
                        <option value="">Unassigned</option>
                        {teachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>
                                {teacher.firstName} {teacher.lastName}
                            </option>
                        ))}
                    </select>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-light" onClick={() => setAssignRow(null)}>Cancel</button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!assignRow || savingId === assignRow.id}
                        onClick={() => assignTeacher(assignRow.id, assignTeacherId)}
                    >
                        {savingId === assignRow?.id ? "Saving…" : "Save"}
                    </button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={Boolean(partnerRow)} toggle={() => setPartnerRow(null)} centered>
                <ModalHeader toggle={() => setPartnerRow(null)}>Change partner</ModalHeader>
                <ModalBody>
                    <p className="text-muted">
                        {partnerRow?.child?.firstName} {partnerRow?.child?.lastName}
                        {partnerRow?.partner?.organizationName ? ` · currently ${partnerRow.partner.organizationName}` : ""}
                    </p>
                    <select
                        className="form-select"
                        value={partnerId}
                        onChange={(e) => setPartnerId(e.target.value)}
                    >
                        <option value="">Select partner</option>
                        {partnerOptions.map((partner) => (
                            <option key={partner.id} value={partner.id}>
                                {partner.organizationName}
                            </option>
                        ))}
                    </select>
                    <p className="text-muted mt-2 mb-0">
                        If the current teacher is not on the new partner, the lesson is left unassigned.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-light" onClick={() => setPartnerRow(null)}>Cancel</button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!partnerRow || !partnerId || savingId === partnerRow.id}
                        onClick={() => {
                            void (async () => {
                                try {
                                    setSavingId(partnerRow.id);
                                    const response = await axios.put(`${baseUrl}/admin/personal-class/partner/${partnerRow.id}`, {
                                        partnerId: Number(partnerId),
                                    });
                                    const updated = response.data?.data;
                                    setRows((current) => current.map((row) => (row.id === partnerRow.id ? updated : row)));
                                    toast.success(response.data?.message || "Partner updated");
                                    setPartnerRow(null);
                                } catch (error) {
                                    handleAuthError(error);
                                    toast.error(error?.response?.data?.message || "Unable to change partner");
                                } finally {
                                    setSavingId(null);
                                }
                            })();
                        }}
                    >
                        {savingId === partnerRow?.id ? "Saving…" : "Save"}
                    </button>
                </ModalFooter>
            </Modal>
            <ToastContainer />
        </React.Fragment>
    );
};

export default withAuth(ManagePersonalClass);
